import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowLeft } from 'lucide-react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { UserRound, Users } from 'lucide-react';
import api from '../../services/api';
import PostCard from '../../components/PostCard';
import ProfilePictureUpload from '../../components/ProfilePictureUpload';
import MetaTags from '../../components/MetaTags';
import './Profile.css';

const Profile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPostCount, setTotalPostCount] = useState(0);
  const [user, setUser] = useState<any>(null);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [followStats, setFollowStats] = useState<any>(null);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [followers, setFollowers] = useState<any[]>([]);
  const [following, setFollowing] = useState<any[]>([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Get current user
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    if (username) {
      // Always start at top when navigating to a profile
      window.scrollTo(0, 0);

      // Reset pagination when username changes
      setPage(1);
      setHasMore(true);
      setPosts([]);
      setTotalPostCount(0);

      fetchUserProfile(username);
      fetchUserPosts(username, 1, false);
    }
  }, [username]);

  // Fetch follow stats after profileUser is set
  useEffect(() => {
    if (profileUser && profileUser.id) {
      fetchFollowStats(profileUser.id);
    }
  }, [profileUser]);

  // Listen for profile picture updates to update posts in profile feed
  useEffect(() => {
    const handleProfilePictureUpdate = (event: CustomEvent) => {
      if (user && event.detail.hasOwnProperty('profile_picture_url')) {
        // Update posts that belong to the current user
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.username === user.username
              ? { ...post, profile_picture_url: event.detail.profile_picture_url }
              : post
          )
        );
      }
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);

    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);
    };
  }, [user]);

  const fetchUserProfile = async (username: string) => {
    try {
      const response = await api.get(`/profile/${username}`);
      setProfileUser(response.data.user);
      setNotFound(false);
    } catch (error) {
      // Failed to fetch user profile
      // If user doesn't exist, mark as not found
      // @ts-ignore
      const status = (error && error.response && error.response.status) || 0;
      if (status === 404) {
        setNotFound(true);
      } else {
        setNotFound(true);
      }
    }
  };

  const fetchUserPosts = async (username: string, pageNum: number = 1, append: boolean = false) => {
    // Prevent duplicate requests
    if ((pageNum === 1 && loading) || (pageNum > 1 && loadingMore)) {
      return;
    }

    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Direct API call with pagination
      const response = await api.get(`/posts/user/${username}?page=${pageNum}&limit=10`);
      const { posts: newPosts, pagination, totalCount } = response.data;

      // Update total count
      setTotalPostCount(totalCount || 0);

      if (append && pageNum > 1) {
        // Append new posts to existing ones
        setPosts((prevPosts) => [...prevPosts, ...newPosts]);
      } else {
        // Replace posts (initial load or refresh)
        setPosts(newPosts);
      }

      setPage(pageNum);
      setHasMore(pagination.hasMore);
    } catch (error) {
      // Failed to fetch user posts
    } finally {
      if (pageNum === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const fetchFollowStats = async (userId: string) => {
    try {
      const response = await api.get(`/follows/${userId}/stats`);
      setFollowStats(response.data);
      setIsFollowing(response.data.is_following);
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchFollowers = async (userId: string) => {
    setLoadingFollowers(true);
    try {
      const response = await api.get(`/follows/${userId}/followers`);
      setFollowers(response.data.followers);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoadingFollowers(false);
    }
  };

  const fetchFollowing = async (userId: string) => {
    setLoadingFollowing(true);
    try {
      const response = await api.get(`/follows/${userId}/following`);
      setFollowing(response.data.following);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoadingFollowing(false);
    }
  };

  // Infinite scroll handler for window scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

      // Load more when user has scrolled 70% of the way down
      if (scrollPercentage > 70 && hasMore && !loadingMore && !loading) {
        fetchUserPosts(username!, page + 1, true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, loading, username, page]);

  // Prevent background scroll when modals are open
  useEffect(() => {
    if (showFollowersModal || showFollowingModal || showUnfollowModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showFollowersModal, showFollowingModal, showUnfollowModal]);

  const handleFollowToggle = async () => {
    if (!profileUser) return;

    if (isFollowing) {
      // Show unfollow confirmation modal
      setShowUnfollowModal(true);
    } else {
      // Follow immediately
      const originalIsFollowing = isFollowing;
      const originalFollowersCount = followStats?.followers_count || 0;

      // Optimistic update - immediately change UI
      setIsFollowing(true);
      setFollowStats((prev: any) => ({
        ...prev,
        followers_count: prev.followers_count + 1,
        is_following: true
      }));

      try {
        await api.post(`/follows/${profileUser.id}`);
      } catch (error) {
        // Rollback on error
        setIsFollowing(originalIsFollowing);
        setFollowStats((prev: any) => ({
          ...prev,
          followers_count: originalFollowersCount,
          is_following: originalIsFollowing
        }));
      }
    }
  };

  const handleUnfollowConfirm = async () => {
    if (!profileUser) return;

    // Immediately close the modal
    setShowUnfollowModal(false);

    const originalIsFollowing = isFollowing;
    const originalFollowersCount = followStats?.followers_count || 0;

    // Optimistic update - immediately change UI
    setIsFollowing(false);
    setFollowStats((prev: any) => ({
      ...prev,
      followers_count: Math.max(0, prev.followers_count - 1),
      is_following: false
    }));

    try {
      await api.delete(`/follows/${profileUser.id}`);
    } catch (error) {
      // Rollback on error
      setIsFollowing(originalIsFollowing);
      setFollowStats((prev: any) => ({
        ...prev,
        followers_count: originalFollowersCount,
        is_following: originalIsFollowing
      }));
    }
  };

  const handleFollowersClick = () => {
    if (profileUser) {
      fetchFollowers(profileUser.id);
      setShowFollowersModal(true);
    }
  };

  const handleFollowingClick = () => {
    if (profileUser) {
      fetchFollowing(profileUser.id);
      setShowFollowingModal(true);
    }
  };


  const isOwnProfile = user?.username === username;

  // Format large numbers with K/M suffixes (truncate, don't round)
  const formatCount = (count: number): string => {
    if (count >= 1000000) {
      const millions = Math.floor(count / 100000) / 10; // Truncate to 1 decimal
      return millions.toString().replace(/\.0$/, '') + 'M';
    } else if (count >= 10000) {
      const thousands = Math.floor(count / 100) / 10; // Truncate to 1 decimal
      return thousands.toString().replace(/\.0$/, '') + 'K';
    }
    return count.toString();
  };

  const handleBackClick = () => {
    // Always try to use browser's back functionality first
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    // Fallback to sessionStorage-based navigation
    const previousLocation = sessionStorage.getItem('previousLocation');

    if (previousLocation) {
      navigate(previousLocation);
    } else {
      // Ultimate fallback to feed
      navigate('/feed');
    }
  };

  const handleProfilePictureUpdate = (newUrl: string | null) => {
    // Update the profile user state
    setProfileUser((prev: any) => ({
      ...prev,
      profile_picture_url: newUrl
    }));

    // If it's the current user's profile, also update the user state
    if (isOwnProfile) {
      setUser((prev: any) => ({
        ...prev,
        profile_picture_url: newUrl
      }));

      // Update localStorage
      const updatedUser = { ...user, profile_picture_url: newUrl };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
        detail: { profile_picture_url: newUrl }
      }));
    }
  };


  const handlePostLiked = (postId: string, liked: boolean, actualCount: number) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId
          ? {
            ...post,
            is_liked: liked,
            likes_count: actualCount
          }
          : post
      )
    );
  };

  const handlePostDeleted = (postId: string) => {
    setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
    setTotalPostCount(prev => Math.max(0, prev - 1));
  };

  const handlePostEdited = (postId: string, newContent: string) => {
    setPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId
          ? { ...post, post_content: newContent }
          : post
      )
    );
  };


  useEffect(() => {
    if (username) {
      // Disable browser auto-scroll when returning from post detail
      const savedScrollPositionForyou = sessionStorage.getItem('feedScrollPosition_foryou');
      const savedScrollPositionFollowing = sessionStorage.getItem('feedScrollPosition_following');
      const savedScrollPosition = savedScrollPositionForyou || savedScrollPositionFollowing;
      const fromPostDetail = location.state?.fromPostDetail;

      if (savedScrollPosition && fromPostDetail) {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual';
        }
      }

      fetchUserPosts(username);
    }
  }, [username]);

  // Restore scroll position after posts load
  useEffect(() => {
    const savedScrollPositionForyou = sessionStorage.getItem('feedScrollPosition_foryou');
    const savedScrollPositionFollowing = sessionStorage.getItem('feedScrollPosition_following');
    const savedScrollPosition = savedScrollPositionForyou || savedScrollPositionFollowing;
    const fromPostDetail = location.state?.fromPostDetail;

    if (savedScrollPosition && fromPostDetail && posts.length > 0) {
      // Use requestAnimationFrame to ensure DOM is painted
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
        // Clean up both feed type scroll positions
        sessionStorage.removeItem('feedScrollPosition_foryou');
        sessionStorage.removeItem('feedScrollPosition_following');
        sessionStorage.removeItem('previousLocation');
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'auto';
        }
      });
    }
  }, [posts, location.state?.fromPostDetail]);

  // Skeleton data arrays
  const skeletonStats = ['Posts', 'Followers', 'Following'];

  const renderSkeletonStats = () => {
    return skeletonStats.map((label, index) => (
      <div key={index} className="profile-skeleton-stat-item">
        <div className="profile-skeleton-stat-number"></div>
        <div className="profile-skeleton-stat-label"></div>
      </div>
    ));
  };

  const renderSkeletonPosts = () => {
    return Array.from({ length: 7 }, (_, index) => (
      <div key={index} className="profile-skeleton-post">
        <div className="profile-skeleton-post-header">
          <div className="profile-skeleton-post-avatar"></div>
          <div className="profile-skeleton-post-info">
            <div className="profile-skeleton-post-username"></div>
            <div className="profile-skeleton-post-time"></div>
          </div>
        </div>
        <div className="profile-skeleton-post-content">
          <div className="profile-skeleton-post-line"></div>
          <div className="profile-skeleton-post-line short"></div>
        </div>
      </div>
    ));
  };

  // Not Found state
  if (notFound) {
    return (
      <div className="profile-container">
        <div className="profile-not-found">
          <h2 className="profile-not-found-title">The link's not working or the page is gone.</h2>
          <p className="profile-not-found-subtitle">Go back to keep exploring.</p>
          <button className="profile-not-found-back" onClick={handleBackClick}>Back</button>
        </div>
      </div>
    );
  }

  // Force loading state for CSS testing - remove this line when done testing
  if (loading && posts.length === 0) {
    return (
      <div className="profile-container">
        <div className="profile-skeleton">
          {/* Profile Header Skeleton - matches actual profile structure */}
          <div className="profile-skeleton-header">
            <div className="profile-skeleton-avatar-section">
              <div className="profile-skeleton-avatar"></div>
            </div>

            <div className="profile-skeleton-info">
              <div className="profile-skeleton-username-section">
                <div className="profile-skeleton-username"></div>
              </div>

              <div className="profile-skeleton-stats-section">
                <div className="profile-skeleton-stats">
                  {renderSkeletonStats()}
                </div>
              </div>
            </div>
          </div>

          {/* Posts Skeleton - matches .profile-posts structure */}
          <div className="profile-skeleton-posts">
            {renderSkeletonPosts()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaTags
        title={`${profileUser?.username || username} - Mindcast`}
        description="The Playground for Curious Minds"
        canonicalUrl={`https://mindcast.app/profile/${username}`}
      />
      <div className="profile-container">
        <div className='profile-user-info'>


          <div className="profile-header">
            {!isOwnProfile && (
              <button onClick={handleBackClick} className="profile-back-button">
                <ArrowLeft className="back-icon" />
              </button>
            )}
            <div className="profile-avatar-section">
              {isOwnProfile ? (
                <ProfilePictureUpload
                  currentProfilePicture={profileUser?.profile_picture_url}
                  onProfilePictureUpdate={handleProfilePictureUpdate}
                  username={username || ''}
                />
              ) : (
                <div className="profile-avatar profile-avatar-other">
                  {profileUser?.profile_picture_url ? (
                    <img
                      src={profileUser.profile_picture_url}
                      alt={`${username}'s profile`}
                      className="profile-avatar-image"
                    />
                  ) : (
                    <UserRound className="profile-avatar-icon" />
                  )}
                </div>
              )}
            </div>


            <div className="profile-info">
              <div className="profile-username">
                <h1>@{username}</h1>

              </div>

              <div className="profile-stats-section">
                <div className="profile-stats">
                  <div className="profile-stat-item">
                    <span className="profile-stat-number">{formatCount(totalPostCount)}</span>
                    <span className="profile-stat-label">posts</span>
                  </div>
                  <div className="profile-stat-item" onClick={handleFollowersClick}>
                    <span className="profile-stat-number">{formatCount(followStats?.followers_count || 0)}</span>
                    <span className="profile-stat-label">followers</span>
                  </div>
                  <div className="profile-stat-item" onClick={handleFollowingClick}>
                    <span className="profile-stat-number">{formatCount(followStats?.following_count || 0)}</span>
                    <span className="profile-stat-label">following</span>
                  </div>
                </div>

                <div className="profile-follow-button-section">
                  {isOwnProfile ? (
                    <button
                      onClick={() => navigate('/settings')}
                      className={"profile-follow-button profile-following"}
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <button
                      onClick={handleFollowToggle}
                      className={`profile-follow-button ${isFollowing ? 'profile-following' : 'profile-follow'}`}
                    >
                      {isFollowing ? (
                        <>
                          Following
                          <ChevronDown className="profile-follow-icon" />
                        </>
                      ) : (
                        <>
                          Follow
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>


        <div className="profile-posts">
          {posts.length === 0 ? (
            <div className="empty-profile">
              <h3>No posts yet</h3>
              <p>
                {isOwnProfile
                  ? "You haven't posted anything yet. Share your first thought!"
                  : `@${username} hasn't posted anything yet.`
                }
              </p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onLikeToggle={handlePostLiked}
                  onDelete={handlePostDeleted}
                  onEdit={handlePostEdited}
                />
              ))}
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#888888' }}>
                  Loading more posts...
                </div>
              )}
            </>
          )}
        </div>

        {/* Followers Modal */}
        {showFollowersModal && (
          <div className="profile-followers-modal-overlay">
            <div className="profile-followers-modal-content">
              <div className="profile-followers-modal-header">
                <h3 className="profile-followers-modal-title">Followers</h3>
                <button
                  onClick={() => setShowFollowersModal(false)}
                  className="profile-followers-modal-close"
                >
                  ×
                </button>
              </div>
              <div className="profile-followers-modal-body">
                {loadingFollowers ? (
                  <div className="profile-followers-loading">
                    <div className="profile-followers-spinner"></div>
                    <p>Loading followers...</p>
                  </div>
                ) : followers.length === 0 ? (
                  <div className="profile-followers-empty">
                    <Users className="profile-followers-empty-icon" />
                    <p>No followers yet</p>
                  </div>
                ) : (
                  <div className="profile-followers-list">
                    {followers.map((follower) => (
                      <div
                        key={follower.id}
                        className="profile-follower-item"
                        onClick={() => {
                          setShowFollowersModal(false);
                          navigate(`/profile/${follower.username}`);
                        }}
                      >
                        <div className="profile-follower-avatar">
                          {follower.profile_picture_url ? (
                            <img
                              src={follower.profile_picture_url}
                              alt={`${follower.username}'s profile`}
                              className="profile-follower-avatar-image"
                            />
                          ) : (
                            <UserRound className="profile-follower-avatar-icon" />
                          )}
                        </div>
                        <div className="profile-follower-info">
                          <span className="profile-follower-username">@{follower.username}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Following Modal */}
        {showFollowingModal && (
          <div className="profile-following-modal-overlay">
            <div className="profile-following-modal-content">
              <div className="profile-following-modal-header">
                <h3 className="profile-following-modal-title">Following</h3>
                <button
                  onClick={() => setShowFollowingModal(false)}
                  className="profile-following-modal-close"
                >
                  ×
                </button>
              </div>
              <div className="profile-following-modal-body">
                {loadingFollowing ? (
                  <div className="profile-following-loading">
                    <div className="profile-following-spinner"></div>
                    <p>Loading following...</p>
                  </div>
                ) : following.length === 0 ? (
                  <div className="profile-following-empty">
                    <Users className="profile-following-empty-icon" />
                    <p>Not following anyone yet</p>
                  </div>
                ) : (
                  <div className="profile-following-list">
                    {following.map((followedUser) => (
                      <div
                        key={followedUser.id}
                        className="profile-following-item"
                        onClick={() => {
                          setShowFollowingModal(false);
                          navigate(`/profile/${followedUser.username}`);
                        }}
                      >
                        <div className="profile-following-avatar">
                          {followedUser.profile_picture_url ? (
                            <img
                              src={followedUser.profile_picture_url}
                              alt={`${followedUser.username}'s profile`}
                              className="profile-following-avatar-image"
                            />
                          ) : (
                            <UserRound className="profile-following-avatar-icon" />
                          )}
                        </div>
                        <div className="profile-following-info">
                          <span className="profile-following-username">@{followedUser.username}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Unfollow Confirmation Modal */}
        {showUnfollowModal && (
          <div className="profile-unfollow-modal-overlay">
            <div className="profile-unfollow-modal-content">
              <div className="profile-unfollow-modal-header">
                <h3 className="profile-unfollow-modal-title">Unfollow</h3>
              </div>
              <div className="profile-unfollow-modal-body">
                <p>Are you sure you want to unfollow @{username}? You'll stop seeing their posts in your Following feed.</p>
              </div>
              <div className="profile-unfollow-modal-footer">
                <button
                  onClick={() => setShowUnfollowModal(false)}
                  className="profile-unfollow-modal-button cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnfollowConfirm}
                  className="profile-unfollow-modal-button unfollow-confirm"
                >
                  Unfollow
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default Profile;